/**
 * DyoCartAddCoupon Experiment Script
 * ================================================================================
 * This script implements the "auto-add-coupon-on-cart" experiment using Statsig.
 *
 * Functionality:
 * - Reads the "add_coupon" boolean flag from the "add-coupon-on-cart" Statsig layer.
 * - If enabled, automatically applies a coupon to the Shopify cart using Shopify's cart and discount endpoints.
 * - Makes API calls to Shopify to fetch and update the cart and apply discounts.
 * - Includes logging and error handling for experiment monitoring.
 *
 * This is a specific implementation for auto-applying coupons and is not a generic template.
 * Please update this file directly for changes to this experiment.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getStatsigClient, getOrCreateStatsigManager, Logger, syncDebugFlag, } from './statsig-manager.js';
const EXPERIMENT = {
    title: "DyoCartAddCoupon",
    name: "auto-add-coupon-on-cart",
    gate: "add-coupon-on-cart",
    layer: "add-coupon-on-cart",
};
const DEBUG = false; // Altere para true para habilitar logs deste teste
const SESSION_STORAGE_KEY = "statsig:autoDiscount";
const RUNTIME_LOCK_KEY = "__statsigAutoDiscountRunning";
if (!customElements.get("dyo-add-coupon-cart"))
    customElements.define("dyo-add-coupon-cart", class DyoAddCouponCart extends HTMLElement {
        constructor() {
            super();
            // Lê os atributos HTML data-* que alimentam o experimento
            this.customerId = this.getAttribute("data-customer-id") || null;
            this.statsigGate = this.getAttribute("data-statsig-gate") || EXPERIMENT.gate;
            this.statsigLayer = this.getAttribute("data-statsig-layer") || EXPERIMENT.layer;
            this.statsigExperiment = this.getAttribute("data-statsig-experiment") || this.statsigLayer || "auto-add-coupon-on-cart";
            this.statsigStableId = null;
            this.allocatedExperiment = null;
            this.obgExperimentInfos = {
                stable_id: this.statsigStableId,
                gate: this.statsigGate,
                layer: this.statsigLayer,
                customer_id: this.customerId,
                allocated_experiment: this.allocatedExperiment,
                category: "Statsig",
                component: `${EXPERIMENT.name}`,
            };
            this.urlParams = new URLSearchParams(window.location.search);
        }
        /**
         * Método nativo do Web Component chamado quando o elemento é inserido no DOM.
         * Aqui iniciamos o teste, aguardando o Statsig estar disponível.
         */
        connectedCallback() {
            return __awaiter(this, void 0, void 0, function* () {
                // DEBUG: garante log mesmo se Logger estiver desabilitado
                console.info(`[${EXPERIMENT.name}] connectedCallback invoked`, { search: window.location.search });
                // Checagens de segmentação/execução
                const params = this.urlParams;
                const hasSegmentParam = params.get("testSegment") === "autoDiscount";
                const alreadyOnboarded = sessionStorage.getItem(SESSION_STORAGE_KEY) === "true";
                const hasCouponParam = !!this.getCouponFromUrl();
                // 1) Se já tem sessionStorage setado, **não executa novamente** o teste
                if (alreadyOnboarded) {
                    if (DEBUG)
                        console.info(`[Statsig][${EXPERIMENT.name}] Session flag present; skipping test run.`);
                    return; // sai sem inicializar Statsig ou rodar o teste
                }
                // 2) No primeiro acesso, **exige** testSegment=autoDiscount na URL
                if (!hasSegmentParam) {
                    if (DEBUG)
                        console.info(`[Statsig][${EXPERIMENT.name}] Missing testSegment=autoDiscount; skipping entirely.`);
                    return; // sem parâmetro, não faz nada
                }
                // 2.1) Se tem segment na URL mas **não** tem cupom, não executa o teste
                if (!hasCouponParam) {
                    if (DEBUG)
                        console.info(`[Statsig][${EXPERIMENT.name}] testSegment presente, mas parâmetro 'cupom' ausente; skipping.`);
                    return;
                }
                // 2.2) Lock em memória por pageview para evitar que múltiplas instâncias
                // do componente rodem o fluxo ao mesmo tempo antes de o sessionStorage ser gravado.
                const globalScope = window;
                if (globalScope[RUNTIME_LOCK_KEY]) {
                    if (DEBUG)
                        console.info(`[Statsig][${EXPERIMENT.name}] Runtime lock already acquired; skipping duplicate instance.`);
                    return;
                }
                globalScope[RUNTIME_LOCK_KEY] = true;
                // 3) Primeiro acesso E com parâmetro: persiste o sinal e executa **apenas uma vez**
                try {
                    sessionStorage.setItem(SESSION_STORAGE_KEY, "true");
                    if (DEBUG)
                        console.info(`[Statsig][${EXPERIMENT.name}] testSegment=autoDiscount detected; session flag saved.`);
                }
                catch (err) {
                    Logger.warn(EXPERIMENT.name, 'Failed to set sessionStorage', err);
                }
                syncDebugFlag(EXPERIMENT.name, DEBUG);
                try {
                    yield getOrCreateStatsigManager();
                    this.runTest();
                }
                catch (error) {
                    Logger.error(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] StatsigManager not ready`, error, this.obgExperimentInfos);
                }
            });
        }
        /**
         * Fluxo principal do experimento.
         * Verifica o gate, lê o booleano do layer e aplica layout se necessário.
         */
        runTest() {
            return __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c;
                Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Starting experiment...`, this.obgExperimentInfos);
                // DEBUG: log direto no console para garantir visibilidade
                console.info(`[${EXPERIMENT.name}] runTest start`, { search: window.location.search });
                try {
                    const stableContext = yield ((_a = getStatsigClient()) === null || _a === void 0 ? void 0 : _a.getAsyncContext());
                    this.statsigStableId = (_b = stableContext === null || stableContext === void 0 ? void 0 : stableContext.stableID) !== null && _b !== void 0 ? _b : null;
                    const experimentManager = yield getOrCreateStatsigManager();
                    Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Experiment created: `, EXPERIMENT);
                    const gateOpen = yield experimentManager.checkGate(this.statsigGate);
                    if (!gateOpen) {
                        Logger.warn(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Gate closed. Exiting.`, this.obgExperimentInfos);
                        return;
                    }
                    const layer = yield experimentManager.getLayerParams(this.statsigLayer);
                    Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Loaded layer`, {
                        layerName: this.statsigLayer,
                        layerKeys: layer ? Object.keys((_c = layer.value) !== null && _c !== void 0 ? _c : {}) : [],
                    });
                    try {
                        const client = getStatsigClient();
                        const exp = client && client.getExperiment ? client.getExperiment(this.statsigExperiment) : null;
                        const expGroupName = exp && typeof exp.getGroupName === 'function' ? exp.getGroupName() : undefined;
                        const expValues = exp && typeof exp.getValue === 'function' ? exp.getValue() : undefined;
                        Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Experiment probe`, { experiment: this.statsigExperiment, groupName: expGroupName, values: expValues });
                    }
                    catch (e) {
                        Logger.warn(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Experiment probe failed`, e);
                    }
                    // Log extra info: group name if available
                    const groupName = (layer && typeof layer.getGroupName === 'function') ? layer.getGroupName() : undefined;
                    Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Group name from layer`, groupName);
                    if (!layer) {
                        Logger.warn(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Layer not found. Exiting.`, this.obgExperimentInfos);
                        return;
                    }
                    const rawFlag = layer.get("add_coupon");
                    const shouldApply = (typeof rawFlag === 'boolean')
                        ? rawFlag
                        : ["true", "1", "yes", "on"].includes(String(rawFlag).toLowerCase());
                    Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Flag "add_coupon" raw:`, { value: rawFlag, type: typeof rawFlag, coerced: shouldApply });
                    if (!shouldApply) {
                        Logger.warn(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Flag "add_coupon" resolved to false. Exiting.`, Object.assign(Object.assign({}, this.obgExperimentInfos), { rawFlag }));
                        return;
                    }
                    // 4) aplicar alterações de UI
                    yield this.applyLayoutChanges();
                    Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Applied layout based on boolean flag.`);
                }
                catch (error) {
                    Logger.error(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Error in runTest()`, error, this.obgExperimentInfos);
                }
            });
        }
        /** Retorna o valor do cupom (se existir) já normalizado; caso contrário, null */
        getCouponFromUrl() {
            const params = this.urlParams;
            const code = params.get("cupom");
            return code && code.trim() ? code.trim() : null;
        }
        /**
         * Implemente aqui a alteração de interface desejada quando o flag booleano estiver TRUE.
         * Ex.: adicionar uma classe no body, trocar um texto, mostrar/ocultar um bloco, etc.
         * Use seletores estáveis para evitar flutuações.
         */
        applyLayoutChanges() {
            return __awaiter(this, void 0, void 0, function* () {
                var _a, _b;
                // Liga uma classe global para facilitar CSS/telemetria
                document.documentElement.classList.add(`${EXPERIMENT.name}--on`);
                // Tenta obter o cupom da URL (já validado no início do fluxo)
                const code = this.getCouponFromUrl();
                if (!code) {
                    Logger.warn(`[Statsig][${EXPERIMENT.name}] Sinal verde, mas 'cupom' não encontrado na URL. Abortando aplicação de cupom.`);
                    return;
                }
                let root = (_b = (_a = window === null || window === void 0 ? void 0 : window.Shopify) === null || _a === void 0 ? void 0 : _a.routes) === null || _b === void 0 ? void 0 : _b.root;
                if (!root) {
                    Logger.warn(`[Statsig][${EXPERIMENT.name}] Shopify.routes.root is not available on window. Falling back to '/'. This may indicate a configuration issue or unexpected environment.`);
                    root = '/';
                }
                try {
                    // 1) Salva o cupom como atributo do carrinho para que o checkout possa usá-lo depois
                    const updateRes = yield fetch(`${root}cart/update.js`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept': 'application/json'
                        },
                        credentials: 'same-origin',
                        body: JSON.stringify({ attributes: { coupon: code } }),
                    });
                    if (!updateRes.ok) {
                        const errorPayload = yield updateRes.text().catch(() => '');
                        throw new Error(`Cart attributes update failed: ${updateRes.status} ${updateRes.statusText} ${errorPayload}`);
                    }
                    try {
                        yield updateRes.clone().json();
                    }
                    catch (err) {
                        Logger.warn(`[Statsig][${EXPERIMENT.name}] Failed to read response body`, err);
                    }
                    // 2) (Best-effort) Tenta "precarregar" o desconto na sessão via rota /discount/<code>
                    //    Sem navegação: isso ajuda a manter o usuário na página do carrinho
                    try {
                        const discountRes = yield fetch(`${root}discount/${encodeURIComponent(code)}?redirect=/cart`, {
                            method: 'GET',
                            credentials: 'same-origin',
                        });
                        if (!discountRes.ok) {
                            Logger.warn(`[Statsig][${EXPERIMENT.name}] Discount pre-load returned ${discountRes.status}`);
                        }
                    }
                    catch (err) {
                        Logger.warn(`[Statsig][${EXPERIMENT.name}] Failed to pre-load discount URL`, err);
                    }
                    sessionStorage.setItem('applied_coupon_code', code);
                    Logger.log(EXPERIMENT.name, `[Statsig][${EXPERIMENT.name}] Coupon applied from URL.`, code);
                }
                catch (error) {
                    Logger.error(`[Statsig][${EXPERIMENT.name}] Failed to apply coupon from URL.`, error, code);
                }
            });
        }
    });
